import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    WritableSignal,
    effect,
    inject,
    signal,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { OfferChallengeBlock } from "@playcharge/common/models/backend/offers/offer-challenge-block";
import { OfferChallengeForm, OfferChallengesForm } from "../../../models/offers/offer-forms";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { OfferStore } from "../../../stores/offers/offer.store";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { MatInputModule } from "@angular/material/input";
import { UpdateOfferChallengeBlocksEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-challenge-blocks-edit-model";
import { ToastrService } from "ngx-toastr";
import { FilterPipe } from "../../../pipes/filter.pipe";
import { BackOfficeOfferGoalViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-goal-view-model";
import { CampaignOfferGoalType } from "@playcharge/common/models/backend/offers/campaign-offer-goal-type";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-offer-challenges",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        FilterPipe,
        NoScrollDirective,
    ],
    providers: [OfferStore],
    templateUrl: "./offer-challenges.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferChallengesComponent {
    private lastChallengeBlockId: number = 0;

    private _toastrService: ToastrService = inject(ToastrService);

    public form: FormGroup<OfferChallengesForm> = new FormGroup<OfferChallengesForm>({
        challengeBlocks: new FormArray<FormGroup<OfferChallengeForm>>([]),
    });

    public offerStore: OfferStore = inject(OfferStore);

    public dataSource: MatTableDataSource<unknown> = new MatTableDataSource<unknown>();
    public displayedColumns: WritableSignal<string[]> = signal<string[]>([
        "id",
        "duration",
        "goals",
        "extraReward",
        "extraExpReward",
        "order",
        "actions",
    ]);

    constructor() {
        effect(() => this.offerChangeEffectFn(), { allowSignalWrites: true });
    }

    public addChallengeBlock(challenge: Nullable<OfferChallengeBlock>): void {
        const blockId: number = challenge?.blockId ?? ++this.lastChallengeBlockId;
        const offerGoalsIds: number[] =
            this.offerStore
                .offer()
                ?.goals?.filter((x) => x.challengeBlockId == blockId)
                .map((x) => x.id) ?? [];

        this.form.controls.challengeBlocks.push(
            new FormGroup<OfferChallengeForm>({
                blockId: new FormControl<number>(blockId, {
                    nonNullable: true,
                }),
                duration: new FormControl<Nullable<string>>(challenge?.duration),
                extraReward: new FormControl<number>(challenge?.extraReward ?? 0, {
                    nonNullable: true,
                }),
                extraExpReward: new FormControl<number>(challenge?.extraExpReward ?? 0, {
                    nonNullable: true,
                }),
                offerGoalsIds: new FormControl<number[]>(offerGoalsIds, {
                    nonNullable: true,
                }),
                order: new FormControl<number>(challenge?.order ?? 0, {
                    nonNullable: true,
                }),
            }),
        );

        this.dataSource.data = this.form.controls.challengeBlocks.value;
    }

    public deleteChallengeBlock(challengeBlockId: number): void {
        const index = this.form.controls.challengeBlocks.value.findIndex(
            (x) => x.blockId == challengeBlockId,
        );

        this.form.controls.challengeBlocks.removeAt(index);

        this.dataSource.data = this.form.controls.challengeBlocks.value;
    }

    public updateChallengeBlocks(): void {
        const model: UpdateOfferChallengeBlocksEditModel = {
            challengeBlocks: this.form.getRawValue().challengeBlocks.map((x) => ({
                challengeBlock: {
                    blockId: x.blockId,
                    duration: x.duration,
                    extraReward: x.extraReward,
                    extraExpReward: x.extraExpReward,
                    order: x.order,
                },
                offerGoalsIds: x.offerGoalsIds,
            })),
        };

        this.offerStore.updateOfferChallengeBlocks(model).subscribe({
            next: () => {
                this._toastrService.success("Successfully updated");
            },
            error: (error) => {
                this._toastrService.error(error?.error?.description ?? "Internal Error");
            },
        });
    }

    public filterFn(goal: BackOfficeOfferGoalViewModel): boolean {
        return goal.goalType !== CampaignOfferGoalType.LimitedOffer;
    }

    private offerChangeEffectFn() {
        const offer: Nullable<BackOfficeOfferViewModel> = this.offerStore.offer();

        if (offer) {
            if (offer.challengeBlocks?.length) {
                const sortedChallengeBlocks = offer.challengeBlocks.sort((a, b) => a.order - b.order);
                this.lastChallengeBlockId = Math.max(
                    ...sortedChallengeBlocks.map((x) => x.blockId),
                    0,
                );
                for (const challengeBlock of sortedChallengeBlocks) {
                    this.addChallengeBlock(challengeBlock);
                }
            }
        }
    }
}
